import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import GameIcon from "components/admin/GameIcon";
import { useFetch } from "components/admin/hooks";
import api from "api/client";
import { formatNumber } from "utils/format";
import { NumField, Choice } from "layouts/simulator/fields";

const TIERS = [
  { value: 1, label: "Bậc I" },
  { value: 2, label: "Bậc II" },
  { value: 3, label: "Bậc III" },
];

/** "+1.078 công · +3.125 HP", bỏ chỉ số bằng 0. */
function statText(stats) {
  if (!stats) return "";
  return (
    [
      ["công", stats.atk],
      ["thủ", stats.def],
      ["HP", stats.hp],
      ["MP", stats.mp],
    ]
      .filter(([, v]) => v > 0)
      .map(([label, v]) => `+${formatNumber(v)} ${label}`)
      .join(" · ") || ""
  );
}

function SetRow({ set, tier, lvl, onApply }) {
  const row = set.tiers.find((t) => t.tier === tier);
  const recipe = row ? row.gems : null;

  return (
    <MDBox
      p={2}
      mb={1.5}
      borderRadius="lg"
      sx={({ palette, borders }) => ({
        border: `${borders.borderWidth[1]} solid ${palette.light.main}`,
      })}
    >
      <MDBox display="flex" alignItems="flex-start" gap={1.5}>
        <GameIcon
          path={set.icon || (recipe ? recipe[0].icon : null)}
          size={36}
          fallback="diamond"
        />
        <MDBox flexGrow={1} minWidth={0}>
          <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <MDTypography variant="button" fontWeight="bold">
              {`${set.name} ${row ? row.tierText : ""}`}
            </MDTypography>
            {set.isYinYang && <Chip size="small" color="success" label="cộng chỉ số" />}
          </MDBox>
          <MDBox display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
            {set.elements.map((element, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <Chip key={i} size="small" variant="outlined" label={element.name} />
            ))}
          </MDBox>
          {row && row.text && (
            <MDTypography variant="caption" color="text" display="block" mt={0.75}>
              {row.text}
            </MDTypography>
          )}
          {recipe && (
            <MDTypography variant="caption" color="info" display="block" mt={0.5}>
              {`3 viên: ${recipe.map((g) => g.name).join(", ")}`}
              {row.totalStats && statText(row.totalStats)
                ? ` — cả bộ ${statText(row.totalStats)} ở cấp 0`
                : ""}
            </MDTypography>
          )}
        </MDBox>
        <MDBox flexShrink={0}>
          {recipe ? (
            <MDButton
              size="small"
              variant="gradient"
              color="info"
              onClick={() => onApply(recipe.map((g) => ({ templateId: g.templateId, lvl })))}
            >
              Khảm
            </MDButton>
          ) : (
            <Tooltip title="Cơ sở dữ liệu chưa có đủ mẫu ngọc của bậc này">
              <span>
                <MDButton size="small" variant="outlined" color="secondary" disabled>
                  Thiếu mẫu
                </MDButton>
              </span>
            </Tooltip>
          )}
        </MDBox>
      </MDBox>
    </MDBox>
  );
}

SetRow.propTypes = {
  set: PropTypes.object.isRequired,
  tier: PropTypes.number.isRequired,
  lvl: PropTypes.number.isRequired,
  onApply: PropTypes.func.isRequired,
};

/**
 * Khảm nhanh một bộ ngọc vào cả 3 lỗ của một món.
 *
 * Ba viên lấy từ máy chủ (`GET /api/admin/simulate/gem-sets`) chứ không tự suy ở web: luật ghép bộ
 * có chỗ không đoán được từ bảng hệ — ô tự do của Âm dương, và năm bộ mới phải xét trước Âm dương.
 */
/**
 * Lọc danh sách bộ ngọc.
 *
 * Theo hệ: ô tự do (bộ Âm dương) nhận mọi hệ THƯỜNG, nên bộ đó vẫn khớp khi lọc theo hệ thường,
 * nhưng không khớp khi lọc theo bóng tối / ánh sáng.
 * Theo chỉ số: bỏ bộ không cộng chỉ số đó, rồi xếp bộ cộng nhiều nhất lên trước.
 *
 * Tách riêng khỏi component để kiểm được bằng test mà không phải mô phỏng thao tác chọn của MUI.
 */
export function filterSets(sets, { elements = [], element = "", stat = "", tier = 1 } = {}) {
  let list = sets;
  if (element !== "") {
    const wanted = Number(element);
    const picked = elements.find((e) => e.id === wanted);
    list = list.filter((set) =>
      set.elements.some((e) => e.id === wanted || (e.free && picked && !picked.special))
    );
  }
  if (stat !== "") {
    const value = (set) => {
      const row = set.tiers.find((t) => t.tier === tier);
      return row && row.totalStats ? row.totalStats[stat] || 0 : 0;
    };
    list = list.filter((set) => value(set) > 0).sort((a, b) => value(b) - value(a));
  }
  return list;
}

const STAT_FILTERS = [
  { value: "", label: "Tất cả" },
  { value: "atk", label: "Cộng công" },
  { value: "def", label: "Cộng thủ" },
  { value: "hp", label: "Cộng HP" },
  { value: "mp", label: "Cộng MP" },
];

function GemSetDialog({ slotLabel, maxGemLevel, onClose, onApply }) {
  const { data, loading, error } = useFetch(() => api.get("/api/admin/simulate/gem-sets"), []);
  const [tier, setTier] = useState(3);
  const [lvl, setLvl] = useState(0);
  const [element, setElement] = useState("");
  const [stat, setStat] = useState("");

  const elements = data?.elements || [];

  const sets = useMemo(
    () => filterSets(data?.sets || [], { elements, element, stat, tier }),
    [data, elements, element, stat, tier]
  );

  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose}>
      <DialogTitle>{`Khảm bộ ngọc vào ${slotLabel}`}</DialogTitle>
      <DialogContent dividers>
        {/* flex-start: ô "Cấp ngọc" có chú thích bên dưới nên cao hơn các ô chọn bên cạnh */}
        <Grid container spacing={2} alignItems="flex-start" mb={1}>
          <Grid item xs={6} md={3}>
            <Choice
              label="Bậc ngọc"
              value={tier}
              options={TIERS}
              onChange={(next) => setTier(Number(next))}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <NumField
              label="Cấp ngọc"
              value={lvl}
              min={0}
              max={maxGemLevel}
              onChange={setLvl}
              helper={`tối đa +${maxGemLevel}`}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <Choice
              label="Hệ ngọc"
              value={element}
              options={[
                { value: "", label: "Tất cả" },
                ...elements.map((e) => ({ value: e.id, label: e.name })),
              ]}
              onChange={setElement}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <Choice label="Chỉ số" value={stat} options={STAT_FILTERS} onChange={setStat} />
          </Grid>
          <Grid item xs={12}>
            <MDTypography variant="caption" color="text">
              Ngọc +{maxGemLevel} được tính như bậc cao hơn một nấc khi xác định bậc của bộ, nên có
              hai đường tới bộ bậc III: tiến hoá ngọc, hoặc cường hoá ngọc bậc II lên tối đa.
              {stat !== "" ? " Đang xếp bộ cộng nhiều chỉ số đó nhất lên đầu." : ""}
            </MDTypography>
          </Grid>
        </Grid>

        {error && <Alert severity="error">{error}</Alert>}
        {loading && (
          <MDBox display="flex" justifyContent="center" py={4}>
            <CircularProgress size={24} />
          </MDBox>
        )}
        {!loading && sets.length === 0 && (
          <MDTypography variant="button" color="text">
            Không có bộ nào khớp bộ lọc.
          </MDTypography>
        )}
        {sets.map((set) => (
          <SetRow
            key={set.setId}
            set={set}
            tier={tier}
            lvl={lvl}
            onApply={(gems) => {
              onApply(gems);
              onClose();
            }}
          />
        ))}
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={onClose}>
          Đóng
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

GemSetDialog.propTypes = {
  slotLabel: PropTypes.string.isRequired,
  maxGemLevel: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
};

export default GemSetDialog;
